import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import TekMark, { PoweredByTekflix } from './TekMark';

/**
 * The full landing-page footer, extracted so every marketing page
 * can use the same chrome. Previously LandingPage had this rich
 * footer (5-column sitemap, newsletter, "Small things, well-built."
 * statement, version/socials row) while every other marketing page
 * shipped with a stripped 1-line footer. Inconsistent and visibly
 * downgraded the rest of the site.
 *
 * The sitemap data lives here. Adding a marketing URL = one entry
 * in the right column array, and it shows up everywhere.
 */

const COLUMNS = [
  { h: 'Product', l: [
    ['Payroll',     '/payroll-software'],
    ['Leave',       '/leave-management'],
    ['Employees',   '/employee-management'],
    ['Invoicing',   '/invoice-management'],
    ['Pricing',     '/pricing'],
  ]},
  { h: 'Compare', l: [
    ['vs BambooHR',    '/vs/bamboohr'],
    ['vs Gusto',       '/vs/gusto'],
    ['vs Rippling',    '/vs/rippling'],
    ['vs Zoho People', '/vs/zoho-people'],
  ]},
  { h: 'Alternatives', l: [
    ['BambooHR alternative',    '/bamboohr-alternative'],
    ['Gusto alternative',       '/gusto-alternative'],
    ['Rippling alternative',    '/rippling-alternative'],
    ['Zoho People alternative', '/zoho-people-alternative'],
  ]},
  { h: 'For teams', l: [
    ['Startups',       '/for-startups'],
    ['Agencies',       '/for-agencies'],
    ['Small business', '/for-small-business'],
  ]},
  { h: 'Resources', l: [
    ['Blog',      '/blog'],
    ['Glossary',  '/glossary'],
    ['Security',  '/security'],
    ['About',     '/about'],
    ['Changelog', '/changelog'],
  ]},
];

export default function LandingFooter() {
  return (
    <footer className="tk-footer">
      {/* giant ghost wordmark — anchors the page */}
      <div className="tk-footer-mark" aria-hidden>tekhub.</div>

      <div className="tk-footer-inner">
        {/* Top row — brand + columns + newsletter */}
        <div className="tk-footer-top">
          <div className="tk-footer-brand">
            <Link to="/" className="tk-footer-logo">
              <TekMark size={28} />
            </Link>
            <p className="tk-footer-tag">
              A small, calm workspace for small, focused teams.
              Made with care, shipped on purpose.
            </p>

            <form
              className="tk-footer-news"
              onSubmit={(e) => { e.preventDefault(); }}
            >
              <span className="tk-eyebrow">FOLLOW THE BUILD</span>
              <div className="tk-footer-news-row">
                <input type="email" placeholder="you@company.com" aria-label="Email address" />
                <button type="submit" aria-label="Subscribe">
                  <ArrowRight size={15} />
                </button>
              </div>
              <span className="tk-footer-news-note">A short note, twice a month. Unsubscribe anytime.</span>
            </form>
          </div>

          <div className="tk-footer-cols">
            {COLUMNS.map((col) => (
              <div key={col.h} className="tk-footer-col">
                <div className="tk-eyebrow tk-footer-col-head">{col.h}</div>
                <ul>
                  {col.l.map(([it, href]) => {
                    // External-hash anchors stay as <a>; everything else
                    // is an internal route and gets a real <Link> so SPA
                    // navigation works and crawlers see the URL.
                    const isAnchor = href.startsWith('#') || href.startsWith('http');
                    return (
                      <li key={it}>
                        {isAnchor
                          ? <a href={href}>{it}</a>
                          : <Link to={href}>{it}</Link>}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Closing statement — large editorial line */}
        <div className="tk-footer-statement">
          <span className="tk-display" style={{ fontSize: 'clamp(40px, 5.6vw, 72px)', letterSpacing: '-0.04em', lineHeight: 1 }}>
            Small things,{' '}
            <span className="tk-italic">well-built.</span>
          </span>
        </div>

        {/* Fine print row */}
        <div className="tk-footer-fine">
          <div className="tk-footer-fine-left">
            <span>© {new Date().getFullYear()} TekHub.</span>
            <span className="tk-footer-sep" />
            <PoweredByTekflix size={13} />
            <span className="tk-footer-sep" />
            <Link to="/terms" style={{ color: 'var(--ink-mute)', textDecoration: 'none' }}>Terms</Link>
            <span className="tk-footer-sep" />
            <Link to="/privacy" style={{ color: 'var(--ink-mute)', textDecoration: 'none' }}>Privacy</Link>
          </div>

          <div className="tk-footer-fine-right">
            <span className="tk-footer-status">
              <span className="tk-footer-dot" />
              All systems normal
            </span>
            <span className="tk-footer-sep" />
            <span className="tk-mono">v0.4 · beta</span>
            <span className="tk-footer-sep" />
            <div className="tk-footer-socials">
              {[
                { l: 'X', t: 'Follow on X' },
                { l: 'gh', t: 'GitHub' },
                { l: 'in', t: 'LinkedIn' },
                { l: '@', t: 'Email' },
              ].map((s) => (
                <a key={s.l} href="#" aria-label={s.t}>{s.l}</a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
